import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSearchSuggestions } from '../hooks/useSearchSuggestions';
import { HomeFooter } from '../components/home/HomeFooter';
import { useToast } from '../components/ui/ToastProvider';
import { HomeHeader } from '../components/home/HomeHeader';
import { HomePostCard } from '../components/home/HomePostCard';
import { HomeReviewCard } from '../components/home/HomeReviewCard';
import { HomeSpaceCard } from '../components/home/HomeSpaceCard';
import {
  HomeSpaceExplorer,
  type SpaceFilterState,
} from '../components/home/HomeSpaceExplorer';
import { loadAuthSession } from '../data/authSession';
import { useHomeFeed } from '../hooks/useHomeFeed';
import { searchRooms, searchVendors, type RoomSearchItem, type VendorSearchItem } from '../api/search';
import { formatVendorRoomCount } from '../data/adapters/homeFromApi';
import { formatDateParam, hasSpecificTimeRange } from '../lib/searchQuery';
import { formatStudioRoomTitle } from '../lib/spaceDisplay';
import { isMockMode } from '../config/publicEnv';

function roomSearchItemToHomeSpace(room: RoomSearchItem) {
  return {
    title: formatStudioRoomTitle(room.studioName, room.roomName),
    subtitle: room.description || '',
    studio: room.studioName,
    location: room.roadAddress || '',
    price: `${room.pricePerSlot.toLocaleString()}원`,
    rating: '',
    image: room.thumbnailUrl ?? '',
    detailPath: `/spaces/${room.roomSlug || room.roomId}`,
  };
}

function vendorSearchItemToHomeCard(vendor: VendorSearchItem) {
  return {
    slug: vendor.slug || vendor.id,
    name: vendor.name,
    description: vendor.description,
    location: vendor.address,
    roomCount: formatVendorRoomCount(vendor.roomCount),
    rating: '',
    imageUrl: vendor.thumbnailUrl ?? '',
    detailPath: `/vendors/${vendor.slug || vendor.id}`,
  };
}

function HomeEmptyState({ description, title }: { description?: string; title: string }) {
  return (
    <div className="home-empty-state" role="status">
      <p className="home-empty-state__title">{title}</p>
      {description ? <p className="home-empty-state__description">{description}</p> : null}
    </div>
  );
}

export function MainPage({ previewAuthenticated = false }: { previewAuthenticated?: boolean }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hotPosts, recommendedSpaces, reviewCards, vendorCards, loading, error } = useHomeFeed();
  const isAuthenticated = previewAuthenticated || Boolean(loadAuthSession());
  const [headerSearchOpen, setHeaderSearchOpen] = useState(false);
  const [headerSearchQuery, setHeaderSearchQuery] = useState('');
  const headerSearchRef = useRef<HTMLDivElement | null>(null);
  const hotPostsScrollRef = useRef<HTMLDivElement | null>(null);

  const [filteredSpaces, setFilteredSpaces] = useState<RoomSearchItem[] | null>(null);
  const mainFilterRef = useRef<SpaceFilterState>({});
  const [mainFilterKey, setMainFilterKey] = useState(0);

  const handleMainFilterChange = useCallback((filters: SpaceFilterState) => {
    const prev = JSON.stringify(mainFilterRef.current);
    const next = JSON.stringify(filters);
    if (prev === next) return;
    mainFilterRef.current = filters;
    const hasAny =
      filters.category ||
      filters.capacity ||
      filters.date ||
      filters.parking ||
      filters.regions?.length ||
      filters.reservable ||
      filters.keywords?.length;
    if (!hasAny) {
      setFilteredSpaces(null);
      return;
    }
    setMainFilterKey((k) => k + 1);
  }, []);

  const [popularVendors, setPopularVendors] = useState<VendorSearchItem[] | null>(null);

  // HomeHeader fetches /users/me/summary on its own and caches across pages,
  // so MainPage no longer needs to duplicate that work.

  useEffect(() => {
    if (isMockMode()) return;
    searchVendors({ sort: 'popular', size: 8 })
      .then((res) => setPopularVendors(res.items))
      .catch(() => setPopularVendors([]));
  }, []);

  useEffect(() => {
    if (mainFilterKey === 0 || isMockMode()) return;
    const sf = mainFilterRef.current;
    const cleanKeywords = sf.keywords?.map((k) => k.replace(/^#/, '')).filter(Boolean) ?? [];
    searchRooms({
      category: sf.category,
      region: sf.regions?.length ? sf.regions[0] : undefined,
      regions: sf.regions,
      keywords: cleanKeywords.length ? cleanKeywords : undefined,
      capacity: sf.capacity,
      parking: sf.parking,
      // ADR 0002: 날짜를 보내면 gateway 가 실시간 가용성 필터링을 수행한다(dayOfWeek 는 date 에서 재계산).
      date: sf.date ? formatDateParam(sf.date) : undefined,
      startHour: hasSpecificTimeRange(sf.date) ? sf.date.startHour : undefined,
      endHour: hasSpecificTimeRange(sf.date) ? sf.date.endHour : undefined,
      size: 20,
    }).then((res) => setFilteredSpaces(res.rooms)).catch(() => {
      // 이전 결과가 남아 필터가 안 먹은 것처럼 보인다 — 안내 (무음 방지, 2026-08-25).
      showToast({ message: '검색에 실패했습니다. 잠시 후 다시 시도해주세요.', type: 'error' });
    });
  }, [mainFilterKey]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (!headerSearchRef.current?.contains(target)) {
        setHeaderSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, []);

  // HOT 게시물 자동 슬라이드
  useEffect(() => {
    const track = hotPostsScrollRef.current;
    if (!track || hotPosts.length === 0) return;
    const interval = setInterval(() => {
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (track.scrollLeft >= maxScroll - 10) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: 320, behavior: 'smooth' });
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [hotPosts]);

  const { suggestions: filteredSuggestions } = useSearchSuggestions(headerSearchQuery);
  const spacesForSections = filteredSpaces
    ? filteredSpaces.map(roomSearchItemToHomeSpace)
    : recommendedSpaces;
  const popularSpaces = spacesForSections.slice(0, 8);
  const visibleVendorCards = vendorCards.length > 0
    ? vendorCards
    : (popularVendors ?? []).map(vendorSearchItemToHomeCard);
  const homeFeedErrorMessage = error ? '홈 데이터를 불러오지 못했습니다.' : null;

  return (
    <main className="home-page">
      <HomeHeader
        authenticated={isAuthenticated}
        filteredSuggestions={filteredSuggestions}
        onSearchChange={(value) => {
          setHeaderSearchQuery(value);
          setHeaderSearchOpen(Boolean(value.trim()));
        }}
        onSearchClear={() => {
          setHeaderSearchQuery('');
          setHeaderSearchOpen(false);
        }}
        onSearchFocus={() => setHeaderSearchOpen(Boolean(headerSearchQuery.trim()))}
        onSearchSubmit={(value) => {
          const normalizedValue = value.trim();
          if (!normalizedValue) {
            return;
          }
          navigate(`/search?q=${encodeURIComponent(normalizedValue)}`);
        }}
        onSuggestionSelect={(value) => navigate(`/search?q=${encodeURIComponent(value)}`)}
        searchOpen={headerSearchOpen}
        searchQuery={headerSearchQuery}
        searchRef={headerSearchRef}
      />

      <section className="home-hero">
        <div className="home-hero__inner">
          <div className="home-hero__copy">
            <p className="home-hero__headline">당신의 음악, 당신의 공간</p>
            <p className="home-hero__headline">언제 어디서나 한 번에!</p>
          </div>
          <div className="home-hero__visual">
            <div className="home-hero__vinyl" />
            <div className="home-hero__badge home-hero__badge--music">Music</div>
            <div className="home-hero__badge home-hero__badge--space">Space</div>
          </div>
        </div>
      </section>

      <section className="home-search">
        <HomeSpaceExplorer spaces={recommendedSpaces} variant="hero" />
      </section>

      <section className="home-section" id="hot-posts">
        <div className="home-section__heading">
          <h2>
            이달의 HOT 게시물 <span aria-hidden="true">❤️</span>
          </h2>
        </div>
        {loading ? (
          <HomeEmptyState title="게시물을 불러오는 중입니다." />
        ) : hotPosts.length > 0 ? (
          <div className="home-post-carousel">
            <div className="home-post-carousel__track" ref={hotPostsScrollRef}>
              {hotPosts.slice(0, 5).map((post) => (
                <HomePostCard key={post.title} {...post} />
              ))}
            </div>
          </div>
        ) : (
          <HomeEmptyState
            description={homeFeedErrorMessage ? '잠시 후 다시 시도해 주세요.' : '커뮤니티 활동이 쌓이면 이곳에 표시됩니다.'}
            title={homeFeedErrorMessage ?? '아직 HOT 게시물이 없습니다.'}
          />
        )}
      </section>

      <section className="home-section" id="spaces">
        <div className="home-section__heading home-section__heading--stack">
          <h2>이런 공간은 어떠신가요?</h2>
        </div>
        <HomeSpaceExplorer
          onFilterChange={handleMainFilterChange}
          spaces={spacesForSections}
        />
        {!loading && spacesForSections.length === 0 ? (
          <HomeEmptyState
            description={filteredSpaces ? '필터를 조정하거나 다른 키워드로 다시 검색해 보세요.' : '공간 데이터가 등록되면 이 영역에 표시됩니다.'}
            title={filteredSpaces ? '조건에 맞는 공간이 없습니다.' : homeFeedErrorMessage ?? '아직 추천 공간이 없습니다.'}
          />
        ) : null}
      </section>

      <section className="home-section home-section--vendors" id="vendors">
        <div className="home-section__heading home-section__heading--stack">
          <h2>인기 업체</h2>
          <p>검증된 음악 공간 업체를 만나보세요.</p>
        </div>
        {visibleVendorCards.length > 0 ? (
          <div className="home-space-grid">
            {visibleVendorCards.slice(0, 8).map((vendor) => (
              <HomeSpaceCard
                key={vendor.slug}
                detailPath={vendor.detailPath}
                image={vendor.imageUrl}
                location={vendor.location}
                price={vendor.roomCount}
                priceSuffix=""
                rating={vendor.rating}
                studio=""
                subtitle={vendor.description}
                title={vendor.name}
              />
            ))}
          </div>
        ) : (
          <HomeEmptyState
            description="업체 데이터가 등록되면 이 영역에 표시됩니다."
            title={homeFeedErrorMessage ?? '아직 인기 업체가 없습니다.'}
          />
        )}
      </section>

      <section className="home-section home-section--popular-spaces" id="popular-spaces">
        <div className="home-section__heading home-section__heading--stack">
          <h2>인기 공간</h2>
        </div>
        {popularSpaces.length > 0 ? (
          <div className="home-space-grid">
            {popularSpaces.map((space) => (
              <HomeSpaceCard key={space.title} {...space} />
            ))}
          </div>
        ) : (
          <HomeEmptyState
            description="공간 데이터가 등록되면 이 영역에 표시됩니다."
            title={homeFeedErrorMessage ?? '아직 인기 공간이 없습니다.'}
          />
        )}
      </section>

      <section aria-label="공간 호스트 모집" className="home-host-cta">
        <div className="home-host-cta__inner">
          <div className="home-host-cta__text">
            <span className="home-host-cta__badge">밴더 비즈니스</span>
            <p className="home-host-cta__copy">당신의 공간, 밴더와 함께하고 수익 창출을 누려보세요.</p>
          </div>
          <Link className="home-host-cta__link" to="/business/apply">
            <span>신청하기</span>
            <img alt="" aria-hidden="true" className="home-host-cta__arrow" src={`${process.env.PUBLIC_URL ?? ''}/main/host-cta-arrow.svg`} />
          </Link>
          <img alt="" aria-hidden="true" className="home-host-cta__pick" src={`${process.env.PUBLIC_URL ?? ''}/main/host-cta-pick.svg`} />
        </div>
      </section>

      <section className="home-section" id="reviews">
        <div className="home-section__heading home-section__heading--stack">
          <h2>밴더 리얼 후기를 확인하세요.</h2>
          <p>게스트도 후기와 정보를 충분히 보고 공간을 비교할 수 있습니다.</p>
        </div>
        {loading ? (
          <HomeEmptyState title="후기를 불러오는 중입니다." />
        ) : reviewCards.length > 0 ? (
          <div className="home-review-grid">
            {reviewCards.map((review, index) => (
              <HomeReviewCard key={`review-${index}`} {...review} />
            ))}
          </div>
        ) : (
          <HomeEmptyState
            description="후기 데이터가 등록되면 이 영역에 표시됩니다."
            title={homeFeedErrorMessage ?? '아직 등록된 후기가 없습니다.'}
          />
        )}
      </section>

      <section className="home-app-banner">
        <div className="home-app-banner__copy">
          <p className="home-app-banner__eyebrow">bander app</p>
          <h2>당신의 음악을, 당신의 공간에</h2>
          <p>음악인들이 함께하는 음악 생활 커뮤니티</p>
          <div className="home-app-banner__buttons">
            <button type="button">App Store</button>
            <button type="button">Google Play</button>
          </div>
        </div>
        <div className="home-app-banner__phones">
          <img alt="" className="home-app-banner__phones-image" src={`${process.env.PUBLIC_URL ?? ''}/main/app-phones.png`} />
        </div>
      </section>

      <HomeFooter />
    </main>
  );
}
